import { API_V1 } from 'API/APIs/APIv1';
import { LinkedinTypes } from 'enums';
import { EmailAccountTypes, EmailCredentials, OAuthCredentials } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import Logger from 'logger';

export type LinkedinTwoFaVerificationExternalInput =
  | string
  | {
      verificationPIN?: string;
      action?: 'approved';
    };

export const getCountryCodesExternal = async (apiKey: string) => {
  try {
    const response = await API_V1.get('/api/linkedinAccounts/countrycodes', {
      headers: {
        'x-api-key': apiKey,
      },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting country codes!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getLinkedinAccountByIdExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.get(`/api/linkedinAccounts/${accountId}`, {
      headers: {
        'x-api-key': apiKey,
      },
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const createLinkedinAccountExternal = async (
  apiKey: string,
  countryName: string,
  cityName: string,
  userEmail: string,
  userPassword: string,
  liProductType: LinkedinTypes,
  contractName?: string
) => {
  try {
    const response = await API_V1.post(
      '/linkedinAccounts/addLinkedInAccount',
      {
        countryName,
        cityName,
        userEmail,
        userPassword,
        liProductType,
        contractName,
      },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while creating a new linkedin account!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const syncLinkedinAccountExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.post(
      `/api/linkedinAccounts/${accountId}/sync`,
      {},
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while syncing the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/** Soft cancel of a stuck login: stops the job and resets the status — never logs the device out. */
export const resetLinkedinAccountExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.post(
      `/api/linkedinAccounts/${accountId}/reset`,
      {},
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while resetting the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const logoutLinkedinAccountExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.post(
      `/api/linkedinAccounts/${accountId}/logout`,
      {},
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while logging out the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const verifyLinkedinAccountExternal = async (
  apiKey: string,
  accountId: string,
  verificationCode: LinkedinTwoFaVerificationExternalInput
) => {
  try {
    const payload =
      typeof verificationCode === 'string'
        ? { verificationPIN: verificationCode }
        : verificationCode;

    const response = await API_V1.put(
      `/api/linkedinAccounts/${accountId}/setPin`,
      payload,
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while verifying the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountExternal = async (
  apiKey: string,
  accountId: string,
  countryName: string,
  cityName: string,
  userEmail: string,
  userPassword: string,
  liProductType: LinkedinTypes,
  contractName?: string
) => {
  try {
    const response = await API_V1.put(
      `/linkedinAccounts/${accountId}`,
      { countryName, cityName, userEmail, userPassword, liProductType, contractName },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountExternalWithPayload = async (
  apiKey: string,
  accountId: string,
  payload: {
    countryName?: string,
    cityName?: string,
    userEmail?: string,
    userPassword?: string,
    liProductType?: LinkedinTypes,
    contractName?: string
  }
) => {
  try {
    const response = await API_V1.put(
      `/linkedinAccounts/${accountId}`,
      payload,
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountContractExternal = async (apiKey: string, accountId: string, contractName: string) => {
  try {
    const response = await API_V1.put(
      `/linkedinAccounts/${accountId}`,
      { contractName },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s contract!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateLinkedinAccountEmailCredentialsExternal = async (
  apiKey: string,
  accountId: string,
  type: EmailAccountTypes,
  smtp?: EmailCredentials,
  imap?: EmailCredentials,
  oauth?: OAuthCredentials
) => {
  try {
    const smtpPayload = !_.isNil(smtp)
      ? {
        server: smtp.server,
        port: +smtp.port,
        encryption: smtp.encryption,
        username: smtp.username,
        password: smtp.password,
      }
      : undefined;

    const imapPayload = !_.isNil(imap)
      ? {
        server: imap.server,
        port: +imap.port,
        encryption: imap.encryption,
        username: smtp?.username ?? '',
        password: smtp?.password ?? '',
      }
      : undefined;

    const oauthPayload = !_.isNil(oauth)
      ? {
        email: oauth.email,
        code: oauth.code,
      }
      : undefined;

    const response = await API_V1.post(
      `/api/linkedinAccounts/${accountId}/saveEmailConfig`,
      {
        type,
        smtp: smtpPayload,
        imap: imapPayload,
        oauth: oauthPayload,
      },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating linkedin account %s email credentials!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCaptchaExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.get(`/api/linkedinAccounts/${accountId}/getCaptcha`, {
      headers: {
        'x-api-key': apiKey,
      },
      responseType: 'arraybuffer',
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting captcha for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const solveCaptchaExternal = async (apiKey: string, accountId: string, positions: { y: number; x: number }) => {
  try {
    const response = await API_V1.put(
      `/api/linkedinAccounts/${accountId}/solveCaptcha`,
      {
        positions,
      },
      {
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while solving captcha for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const get2FAScreenshotExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.get(`/api/linkedinAccounts/${accountId}/get2FAScreenshot`, {
      headers: { 'x-api-key': apiKey },
      responseType: 'arraybuffer',
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting 2FA screenshot for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getThirdPartyScreenshotExternal = async (apiKey: string, accountId: string) => {
  try {
    const response = await API_V1.get(`/api/linkedinAccounts/${accountId}/getThirdPartyScreenshot`, {
      headers: { 'x-api-key': apiKey },
      responseType: 'arraybuffer',
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while getting third party screenshot for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const thirdPartyClickExternal = async (apiKey: string, accountId: string, positions: { y: number; x: number }) => {
  try {
    const response = await API_V1.put(
      `/api/linkedinAccounts/${accountId}/thirdPartyClick`,
      {
        positions,
      },
      {
        headers: { 'x-api-key': apiKey },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while clicking in third party page for linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const setThirdPartyTextExternal = async (apiKey: string, accountId: string, thirdPartyText: string) => {
  try {
    const response = await API_V1.put(
      `/api/linkedinAccounts/${accountId}/setThirdPartyText`,
      {
        thirdPartyText,
      },
      {
        headers: { 'x-api-key': apiKey },
      }
    );

    return response.data;
  } catch (e) {
    Logger.trace('Error while verifying the linkedin account %s!', accountId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
